import closeIcon from "@loot-os/icons/icons/close";
import usersIcon from "@loot-os/icons/icons/users";
import { type ReactNode, useId } from "react";
import { Icon } from "../../core/Icon.js";

import { IconButton } from "../../core/IconButton.js";
import { TabItem, TabList, TabPanel, Tabs } from "../../core/Tabs.js";
import { cn } from "../../foundations/cn.js";

export interface ChatRoomTab {
  value: string;
  label: string;
  /** Who's in it, formatted: "1,284". */
  count?: string;
}

export interface ChatPanelProps {
  /** The chat's name: "Trainer chat". */
  title: string;
  /** Who's here: `count` is shown ("1,284"), `label` is read ("1,284 online"). */
  online?: { count: string; label: string };
  /** Closes the drawer or sheet it sits in. */
  close?: { label: string; onClick: () => void };
  /** The rooms, as tabs over the transcript. */
  rooms?: {
    label: string;
    value: string;
    onValueChange: (value: string) => void;
    items: readonly ChatRoomTab[];
  };
  /** Above the messages: a ChatDropBanner. */
  banner?: ReactNode;
  /** Above the messages: a ChatPinned. */
  pinned?: ReactNode;
  /** The ChatTranscript. */
  children: ReactNode;
  /** A ChatReplyBar while answering a message. */
  replyBar?: ReactNode;
  /** The ChatComposer. */
  composer: ReactNode;
  className?: string;
}

/**
 * A whole chat, in the order a reader meets it: the header, the rooms, what's coming (a drop, a
 * pinned note), the messages, and the composer. Sits in a ChatDrawer, or docked in a page.
 */
export function ChatPanel({
  title,
  online,
  close,
  rooms,
  banner,
  pinned,
  children,
  replyBar,
  composer,
  className,
}: ChatPanelProps) {
  const titleId = useId();
  const body = (
    <>
      {(banner || pinned) && (
        <div className="flex flex-col gap-3 border-b border-border-subtle px-3 py-3">
          {banner}
          {pinned}
        </div>
      )}
      {children}
    </>
  );
  return (
    <section aria-labelledby={titleId} className={cn("flex h-full min-h-0 flex-col", className)}>
      <header className="grid h-12 shrink-0 grid-cols-[1fr_auto_1fr] items-center border-b border-border-subtle px-2">
        <span>
          {close && (
            <IconButton
              icon={closeIcon}
              label={close.label}
              variant="ghost"
              size="sm"
              onClick={close.onClick}
            />
          )}
        </span>
        <h2
          id={titleId}
          className="font-body text-body-b1-strong tracking-widest text-text-primary uppercase"
        >
          {title}
        </h2>
        {online && (
          <span className="flex items-center justify-end gap-1 pr-1 font-mono text-mono-meta text-text-tertiary">
            <Icon icon={usersIcon} size={14} />
            <span aria-hidden>{online.count}</span>
            <span className="sr-only">{online.label}</span>
          </span>
        )}
      </header>
      {rooms ? (
        <Tabs
          value={rooms.value}
          onValueChange={(value) => rooms.onValueChange(String(value))}
          className="flex min-h-0 flex-1 flex-col"
        >
          <TabList aria-label={rooms.label} className="shrink-0 gap-0 px-3">
            {rooms.items.map((room) => (
              <TabItem
                key={room.value}
                value={room.value}
                className="flex-1 pt-3 pb-2.5 text-center font-mono text-mono-meta tracking-wider uppercase"
              >
                {room.label}
                {room.count && <span className="text-accent-text"> · {room.count}</span>}
              </TabItem>
            ))}
          </TabList>
          <TabPanel value={rooms.value} className="flex min-h-0 flex-1 flex-col outline-none">
            {body}
          </TabPanel>
        </Tabs>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">{body}</div>
      )}
      {replyBar}
      {composer}
    </section>
  );
}
